/**
 * Production cleanup utilities
 * Supprime automatiquement les logs et optimise les performances en production
 */

/**
 * Supprime tous les console.log/warn/error en production
 * Utilise cette fonction pour wrapper les logs pendant le développement
 */
function suppressLogsInProduction() {
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
    // Sauvegarder les méthodes originales pour les erreurs critiques
    const originalError = console.error;
    const originalWarn = console.warn;

    // Supprimer tous les logs sauf les erreurs critiques
    console.log = () => {};
    console.info = () => {};
    console.debug = () => {};
    console.trace = () => {};
    console.table = () => {};
    console.group = () => {};
    console.groupEnd = () => {};
    console.count = () => {};
    console.time = () => {};
    console.timeEnd = () => {};

    // Filtrer les warnings pour ne garder que les critiques
    console.warn = (...args: unknown[]) => {
      const message = args.join(' ');

      // Supprimer les warnings de preload spécifiques
      if (message.includes('was preloaded using link preload but not used')) {
        return;
      }

      // Supprimer les warnings de React Query
      if (message.includes('getCacheConfigByType is deprecated')) {
        return;
      }

      // Garder les warnings critiques
      originalWarn(...args);
    };

    // Filtrer les erreurs pour ne garder que les critiques
    console.error = (...args: unknown[]) => {
      const message = args.join(' ');

      // Supprimer les erreurs 404 d'images non critiques
      if (message.includes('404') && message.includes('images')) {
        return;
      }

      // Garder les erreurs critiques
      originalError(...args);
    };
  }
}

/**
 * Initialise le nettoyage en production
 * Appeler cette fonction au démarrage de l'application
 */
export function initProductionCleanup() {
  if (process.env.NODE_ENV === 'production') {
    suppressLogsInProduction();

    // Supprimer les propriétés de développement
    if (typeof window !== 'undefined') {
      // Supprimer les propriétés de debug de manière sûre
      try {
        const windowWithDevTools = window as unknown as Record<string, unknown>;

        // Vérifier si la propriété existe et peut être supprimée
        if ('__REACT_DEVTOOLS_GLOBAL_HOOK__' in windowWithDevTools) {
          const descriptor = Object.getOwnPropertyDescriptor(windowWithDevTools, '__REACT_DEVTOOLS_GLOBAL_HOOK__');
          if (descriptor && descriptor.configurable !== false) {
            delete windowWithDevTools.__REACT_DEVTOOLS_GLOBAL_HOOK__;
          } else {
            // Si non configurable, la masquer
            windowWithDevTools.__REACT_DEVTOOLS_GLOBAL_HOOK__ = undefined;
          }
        }

        if ('__REDUX_DEVTOOLS_EXTENSION_COMPOSE__' in windowWithDevTools) {
          const descriptor = Object.getOwnPropertyDescriptor(
            windowWithDevTools,
            '__REDUX_DEVTOOLS_EXTENSION_COMPOSE__'
          );
          if (descriptor && descriptor.configurable !== false) {
            delete windowWithDevTools.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;
          } else {
            // Si non configurable, la masquer
            windowWithDevTools.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ = undefined;
          }
        }
      } catch {
        // Ignorer silencieusement les erreurs de suppression en production
        // En développement, on peut afficher le warning pour debug
        // En production, on ignore complètement
      }

      // Masquer les erreurs de réseau non critiques
      window.addEventListener('error', (event) => {
        if (
          event.message.includes('Loading chunk') ||
          event.message.includes('Loading CSS chunk') ||
          event.message.includes('Failed to load resource') ||
          (event.filename && event.filename.includes('/api/graph-photo/'))
        ) {
          event.preventDefault();
        }
      });

      // Masquer les promesses rejetées non critiques
      window.addEventListener('unhandledrejection', (event) => {
        if (
          event.reason?.message?.includes('Loading chunk') ||
          event.reason?.message?.includes('Network request failed') ||
          event.reason?.message?.includes('404') ||
          (typeof event.reason === 'string' && event.reason.includes('/api/graph-photo/'))
        ) {
          event.preventDefault();
        }
      });
    }
  }
}

